// Lightweight API client for Pusha Teez frontend.
// Reads JWT from localStorage (key: pusha_admin_token) and prepends BACKEND_URL.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'pusha_admin_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

function formatDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export async function apiFetch(path, { method = 'GET', body, auth = false, headers = {} } = {}) {
  const finalHeaders = { 'Content-Type': 'application/json', ...headers };
  if (auth) {
    const t = getToken();
    if (t) finalHeaders.Authorization = `Bearer ${t}`;
  }
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  // Some host instrumentations (PostHog, emergent-main.js) wrap fetch and
  // pre-consume the response stream. Clone before reading to avoid the
  // "body stream already read" error.
  let raw = '';
  try {
    raw = await res.clone().text();
  } catch (cloneErr) {
    // Response was already consumed by host fetch instrumentation — try
    // the original stream one more time; if that also fails fall back to ''.
    try {
      raw = await res.text();
    } catch (readErr) {
      if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
        console.warn('apiFetch: could not read response body', readErr || cloneErr);
      }
      raw = '';
    }
  }
  let data = null;
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch (_parseErr) {
      // Non-JSON body (e.g. plain text from an error page) — keep the raw string.
      data = raw;
    }
  }
  if (!res.ok) {
    let message = formatDetail(data && data.detail ? data.detail : data);
    if (!message || message === 'Something went wrong. Please try again.') {
      // Body was consumed by host instrumentation — derive a friendly
      // message from the HTTP status instead of "body stream already read".
      const fallbacks = {
        400: 'Please double-check the form fields and try again.',
        401: path.includes('/auth/login') ? 'Invalid email or password.' : 'Your session expired. Please sign in again.',
        403: 'You do not have permission to perform this action.',
        404: 'The requested item was not found.',
        409: 'A conflicting item already exists.',
        422: 'Some fields are invalid. Please check the form.',
        429: 'Too many attempts. Please wait a minute and try again.',
      };
      message = fallbacks[res.status] || `Request failed (HTTP ${res.status}).`;
    }
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  // Auth
  login: (email, password) => apiFetch('/api/auth/login', { method: 'POST', body: { email, password } }),
  me: () => apiFetch('/api/auth/me', { auth: true }),
  logout: () => apiFetch('/api/auth/logout', { method: 'POST', auth: true }),

  // Public
  submitQuote: (form) => apiFetch('/api/quotes', { method: 'POST', body: form }),
  getBlogs: () => apiFetch('/api/blogs'),
  getBlogBySlug: (slug) => apiFetch(`/api/blogs/${encodeURIComponent(slug)}`),
  getContent: (key) => apiFetch(`/api/content/${encodeURIComponent(key)}`),

  // Admin
  adminListQuotes: (statusFilter) => apiFetch(`/api/admin/quotes${statusFilter && statusFilter !== 'all' ? `?status_filter=${statusFilter}` : ''}`, { auth: true }),
  adminQuoteStats: () => apiFetch('/api/admin/quotes/stats', { auth: true }),
  adminUpdateQuoteStatus: (id, status) => apiFetch(`/api/admin/quotes/${id}`, { method: 'PATCH', auth: true, body: { status } }),
  adminDeleteQuote: (id) => apiFetch(`/api/admin/quotes/${id}`, { method: 'DELETE', auth: true }),
  adminListBlogs: () => apiFetch('/api/admin/blogs', { auth: true }),
  adminGetBlog: (id) => apiFetch(`/api/admin/blogs/${id}`, { auth: true }),
  adminCreateBlog: (data) => apiFetch('/api/admin/blogs', { method: 'POST', auth: true, body: data }),
  adminUpdateBlog: (id, data) => apiFetch(`/api/admin/blogs/${id}`, { method: 'PUT', auth: true, body: data }),
  adminDeleteBlog: (id) => apiFetch(`/api/admin/blogs/${id}`, { method: 'DELETE', auth: true }),
  adminGetContent: (key) => apiFetch(`/api/admin/content/${encodeURIComponent(key)}`, { auth: true }),
  adminPutContent: (key, data) => apiFetch(`/api/admin/content/${encodeURIComponent(key)}`, { method: 'PUT', auth: true, body: { data } }),

  // Direct CSV
  exportQuotesCsvUrl: () => `${BACKEND_URL}/api/admin/quotes/export.csv`,
};

export { BACKEND_URL };
